import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, CheckCircle, ArrowRight, Award, Zap, Shield, FileText, AlertTriangle, Eye, UserCheck } from 'lucide-react';

export default function SecurityCompliance() {
  const benefits = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "SOC 2 Compliance",
      description: "Complete SOC 2 Type II certification support with automated controls and evidence collection"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Multi-Framework Support",
      description: "PCI-DSS, HIPAA, GDPR, and ISO 27001 compliance frameworks"
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: "Vulnerability Management",
      description: "Automated scanning, patch management, and continuous security monitoring"
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Threat Detection",
      description: "Real-time threat detection and incident response workflows"
    }
  ];

  const complianceFrameworks = [
    {
      name: "SOC 2 Type II",
      description: "Security, availability, processing integrity, confidentiality, and privacy",
      features: ["Automated controls", "Evidence collection", "Continuous monitoring", "Audit support"]
    },
    {
      name: "PCI-DSS",
      description: "Payment Card Industry Data Security Standard compliance",
      features: ["Network segmentation", "Encryption", "Access control", "Quarterly scans"]
    },
    {
      name: "HIPAA",
      description: "Healthcare data protection and privacy requirements",
      features: ["PHI encryption", "Access logging", "BAA management", "Risk assessments"]
    },
    {
      name: "GDPR",
      description: "European data protection and privacy regulations",
      features: ["Data mapping", "Privacy by design", "Breach notification", "Right to deletion"]
    }
  ];

  const securityLayers = [
    {
      layer: "Infrastructure Security",
      items: [
        "Network segmentation and VPC isolation",
        "Security groups and firewall rules",
        "DDoS protection and WAF",
        "Bastion hosts and jump servers"
      ]
    },
    {
      layer: "Application Security",
      items: [
        "SAST and DAST scanning",
        "Dependency vulnerability scanning",
        "Secrets management (Vault, AWS Secrets Manager)",
        "API security and rate limiting"
      ]
    },
    {
      layer: "Data Security",
      items: [
        "Encryption at rest and in transit",
        "KMS and key rotation",
        "Database access control",
        "Backup encryption and testing"
      ]
    },
    {
      layer: "Identity & Access",
      items: [
        "SSO and MFA implementation",
        "RBAC and least privilege",
        "IAM policy management",
        "Access reviews and auditing"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=1920&auto=format&fit=crop&q=80"
            alt="Security & Compliance"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-indigo-900/90 to-purple-900/95"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white mb-6">
              <Lock className="w-4 h-4 text-red-400" />
              <span>Enterprise Security Solutions</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Security & Compliance
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Achieve SOC 2, PCI-DSS, HIPAA, and GDPR compliance with comprehensive
              security audits, automated monitoring, and incident response.
            </p>

            <div className="flex flex-wrap gap-4">
              <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-8 py-4 rounded-full font-bold transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
                Schedule Consultation <ArrowRight className="w-5 h-5" />
              </a>
              <Link to="/#case-studies"
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold transition-all inline-flex items-center gap-2">
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Enterprise Security & Compliance</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Protect your infrastructure and customer data with industry-leading security practices
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center text-white mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Frameworks */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Compliance Frameworks</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert support for major compliance certifications and regulatory requirements
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {complianceFrameworks.map((framework, idx) => (
              <div key={idx} className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 hover:shadow-xl transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center text-white">
                    <Lock className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-purple-600">{framework.name}</h3>
                </div>
                <p className="text-gray-700 mb-6">{framework.description}</p>
                <ul className="space-y-2">
                  {framework.features.map((feature, featureIdx) => (
                    <li key={featureIdx} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Layers */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Multi-Layer Security Approach</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Defense in depth strategy protecting every layer of your infrastructure
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {securityLayers.map((layer, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-sm mb-4">
                  {idx + 1}
                </div>
                <h3 className="text-lg font-bold mb-4 text-purple-600">{layer.layer}</h3>
                <ul className="space-y-2">
                  {layer.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-start gap-2 text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-1.5 flex-shrink-0"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service Description */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=800&auto=format&fit=crop&q=80"
                alt="Security Operations"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-2xl max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <UserCheck className="w-8 h-8 text-purple-600" />
                  <div className="text-3xl font-bold text-purple-600">25+</div>
                </div>
                <div className="text-sm text-gray-600">Compliance Audits Completed</div>
              </div>
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Enterprise Security Expertise
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                With 25+ successful compliance audits and zero security breaches, I've helped fintech, healthcare, and SaaS companies achieve and maintain SOC 2, PCI-DSS, HIPAA, and GDPR compliance.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Compliance Automation</h4>
                    <p className="text-gray-600">Automated evidence collection, continuous monitoring, and compliance dashboards</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Security Audits</h4>
                    <p className="text-gray-600">Comprehensive infrastructure and application security assessments</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Incident Response</h4>
                    <p className="text-gray-600">24/7 monitoring, automated alerting, and documented IR procedures</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Zero Trust Security</h4>
                    <p className="text-gray-600">Never trust, always verify approach with micro-segmentation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Award className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-4xl font-bold mb-4">Success Story</h2>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20">
            <h3 className="text-3xl font-bold mb-4">SaaS Platform SOC 2 Certification</h3>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  Challenge
                </h4>
                <p className="text-purple-100">Enterprise customers requiring SOC 2 Type II certification before contract signing, no security documentation or controls in place</p>
              </div>
              <div>
                <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  Solution
                </h4>
                <p className="text-purple-100">Implemented comprehensive security controls, automated evidence collection, achieved SOC 2 Type II in 4 months</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { metric: "Certification Time", value: "4 months", improvement: "50% faster" },
                { metric: "Security Score", value: "98/100", improvement: "Industry leading" },
                { metric: "Automated Controls", value: "95%", improvement: "Manual → Auto" },
                { metric: "Security Incidents", value: "0", improvement: "Zero breaches" }
              ].map((result, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-sm text-purple-200 mb-2">{result.metric}</div>
                  <div className="text-3xl font-bold mb-1">{result.value}</div>
                  <div className="text-xs text-green-300">{result.improvement}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Achieve Compliance?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's secure your infrastructure and achieve the compliance certifications your business needs
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-10 py-5 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
              Schedule Free Consultation <ArrowRight className="w-6 h-6" />
            </a>
            <Link to="/"
              className="bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 text-white px-10 py-5 rounded-full font-bold text-lg transition-all inline-flex items-center gap-2">
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
